// Display dates for text shown on the public site, such as "Entries close
// December 24, 2026". The columns are plain text, so the admin turns whatever
// is typed into the one format the page reads well: "Month D, YYYY".
//
// Typed forms it reads (US order when numbers are ambiguous):
//   12242026, 122426, 20261224
//   12/24/2026, 12-24-26, 12.24.2026, 2026-12-24
//   Dec 24 2026, December 24th, 2026, 24 December 2026
//   Dec 24 (no year: the next December 24 from today)

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

export type YMD = { y: number; m: number; d: number };

function valid({ y, m, d }: YMD) {
  if (y < 1900 || y > 2099 || m < 1 || m > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function fullYear(y: string) {
  return y.length === 2 ? 2000 + Number(y) : Number(y);
}

function monthFromName(name: string) {
  const n = name.toLowerCase().replace(/\.$/, "");
  if (n.length < 3) return 0;
  const i = MONTHS.findIndex((m) => m.toLowerCase().startsWith(n) || (n === "sept" && m === "September"));
  return i + 1;
}

// The next occurrence of month/day on or after today, for input without a year.
function nextYear(m: number, d: number, today: Date) {
  const y = today.getFullYear();
  const passed = m < today.getMonth() + 1 || (m === today.getMonth() + 1 && d < today.getDate());
  return passed ? y + 1 : y;
}

export function parseDisplayDate(input: string, today = new Date()): YMD | null {
  const s = input.trim().replace(/\s+/g, " ").replace(/,/g, " ").replace(/\s+/g, " ").trim();
  if (!s) return null;
  let r: YMD | null = null;
  let m: RegExpMatchArray | null;

  if ((m = s.match(/^(\d{8})$/))) {
    const t = m[1];
    const ymd = { y: Number(t.slice(0, 4)), m: Number(t.slice(4, 6)), d: Number(t.slice(6)) };
    r = /^(19|20)/.test(t) && valid(ymd) ? ymd : { m: Number(t.slice(0, 2)), d: Number(t.slice(2, 4)), y: Number(t.slice(4)) };
  } else if ((m = s.match(/^(\d{2})(\d{2})(\d{2})$/))) {
    r = { m: Number(m[1]), d: Number(m[2]), y: fullYear(m[3]) };
  } else if ((m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/))) {
    r = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
  } else if ((m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/))) {
    r = { m: Number(m[1]), d: Number(m[2]), y: fullYear(m[3]) };
  } else if ((m = s.match(/^([A-Za-z]+\.?) (\d{1,2})(?:st|nd|rd|th)?(?: (\d{4}))?$/i))) {
    const mo = monthFromName(m[1]); const d = Number(m[2]);
    if (mo) r = { m: mo, d, y: m[3] ? Number(m[3]) : nextYear(mo, d, today) };
  } else if ((m = s.match(/^(\d{1,2})(?:st|nd|rd|th)? ([A-Za-z]+\.?)(?: (\d{4}))?$/i))) {
    const mo = monthFromName(m[2]); const d = Number(m[1]);
    if (mo) r = { m: mo, d, y: m[3] ? Number(m[3]) : nextYear(mo, d, today) };
  }
  return r && valid(r) ? r : null;
}

export function formatDisplayDate({ y, m, d }: YMD) {
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export function toIsoDate({ y, m, d }: YMD) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// The corrected text, or null when the input is not a date.
export function normalizeDisplayDate(input: string, today = new Date()) {
  const p = parseDisplayDate(input, today);
  return p ? formatDisplayDate(p) : null;
}

export function compareYMD(a: YMD, b: YMD) {
  return (a.y - b.y) || (a.m - b.m) || (a.d - b.d);
}
